import { useCallback, useMemo, useRef, useState } from 'react';
import { icons } from '../../components/ui/Icons';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Select } from '../../components/ui/Select';
import { Alert } from '../../components/ui/Alert';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorState } from '../../components/ui/ErrorState';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { Dialog } from '../../components/ui/Dialog';
import { Position, CreatePositionInput, UpdatePositionInput } from '../../lib/adminApi';
import { requestJson, getErrorMessage, isAbortError } from '../../lib/adminApi';
import { useToast } from '../../components/ui/Toast';

type PositionStatusFilter = 'all' | 'active' | 'inactive';
type PositionSortField = 'name' | 'code' | 'employeeCount' | 'createdAt' | 'updatedAt';
type SortDirection = 'asc' | 'desc';

const POSITION_STATUS_OPTIONS = [
  { value: 'all', label: 'Alle Status' },
  { value: 'active', label: 'Aktiv' },
  { value: 'inactive', label: 'Inaktiv' },
];

const POSITION_SORT_OPTIONS: Array<{ value: PositionSortField; label: string }> = [
  { value: 'name', label: 'Name' },
  { value: 'code', label: 'Kürzel' },
  { value: 'employeeCount', label: 'Mitarbeiter' },
  { value: 'createdAt', label: 'Erstellt' },
  { value: 'updatedAt', label: 'Aktualisiert' },
];

export function PositionManagementPage() {
  const { addToast } = useToast();
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PositionStatusFilter>('all');
  const [sortField, setSortField] = useState<PositionSortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState<Position | null>(null);
  const [form, setForm] = useState<CreatePositionInput>({ name: '', code: '', description: undefined, isActive: true });
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Position | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState<{ variant: 'error'; text: string } | null>(null);

  const requestRef = useRef<AbortController | null>(null);

  const loadPositions = useCallback(async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (statusFilter !== 'all') params.set('status', statusFilter);
      params.set('sortBy', sortField);
      params.set('sortOrder', sortDirection);
      const data = await requestJson<Position[]>(`/positions?${params.toString()}`, { signal: controller.signal });
      setPositions(Array.isArray(data) ? data : []);
    } catch (err) {
      if (!isAbortError(err)) {
        setError(getErrorMessage(err, 'Positionen konnten nicht geladen werden.'));
      }
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
      setLoading(false);
    }
  }, [search, statusFilter, sortField, sortDirection]);

  const handleToggleStatus = async (position: Position) => {
    try {
      await requestJson<Position>(`/positions/${position.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !position.isActive }),
      });
      addToast({
        title: position.isActive ? 'Position deaktiviert' : 'Position aktiviert',
        description: `Die Position "${position.name}" wurde ${position.isActive ? 'deaktiviert' : 'aktiviert'}.`,
        variant: 'success',
      });
      void loadPositions();
    } catch (err) {
      addToast({
        title: 'Fehler',
        description: getErrorMessage(err, 'Status konnte nicht geändert werden.'),
        variant: 'error',
      });
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      await requestJson<Position>('/positions', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      setCreateDialogOpen(false);
      setForm({ name: '', code: '', description: undefined, isActive: true });
      addToast({ title: 'Position erstellt', description: 'Die Position wurde erfolgreich angelegt.', variant: 'success' });
      void loadPositions();
    } catch (err) {
      setFormError(getErrorMessage(err, 'Position konnte nicht erstellt werden.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPosition) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const input: UpdatePositionInput = {};
      if (form.name !== undefined) input.name = form.name;
      if (form.code !== undefined) input.code = form.code;
      if (form.description !== undefined) input.description = form.description;
      if (form.isActive !== undefined) input.isActive = form.isActive;
      await requestJson<Position>(`/positions/${editingPosition.id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      });
      setEditDialogOpen(false);
      setEditingPosition(null);
      setForm({ name: '', code: '', description: undefined, isActive: true });
      addToast({ title: 'Position aktualisiert', description: 'Die Position wurde erfolgreich geändert.', variant: 'success' });
      void loadPositions();
    } catch (err) {
      setFormError(getErrorMessage(err, 'Position konnte nicht aktualisiert werden.'));
    } finally {
      setSubmitting(false);
    }
  };

  const openCreateDialog = () => {
    setForm({ name: '', code: '', description: undefined, isActive: true });
    setFormError(null);
    setCreateDialogOpen(true);
  };

  const openEditDialog = (position: Position) => {
    setEditingPosition(position);
    setForm({
      name: position.name,
      code: position.code,
      description: position.description ?? undefined,
      isActive: position.isActive,
    });
    setFormError(null);
    setEditDialogOpen(true);
  };

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
    setForm({ name: '', code: '', description: undefined, isActive: true });
    setFormError(null);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setEditingPosition(null);
    setForm({ name: '', code: '', description: undefined, isActive: true });
    setFormError(null);
  };

  const initiateDelete = (position: Position) => {
    setDeleteTarget(position);
    setDeleteMessage(null);
    setConfirmDeleteOpen(true);
  };

  const executeDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    setDeleteMessage(null);
    try {
      await requestJson<{ success: boolean }>(`/positions/${deleteTarget.id}`, { method: 'DELETE' });
      setConfirmDeleteOpen(false);
      setDeleteTarget(null);
      addToast({ title: 'Position gelöscht', description: 'Die Position wurde entfernt.', variant: 'success' });
      void loadPositions();
    } catch (err) {
      const message = getErrorMessage(err, 'Position konnte nicht gelöscht werden.');
      setDeleteMessage({ variant: 'error', text: message });
    } finally {
      setDeleteLoading(false);
    }
  };

  const filteredPositions = useMemo(() => {
    const query = search.trim().toLowerCase();
    return positions
      .filter((pos) => {
        const matchesStatus =
          statusFilter === 'all' ||
          (statusFilter === 'active' && pos.isActive) ||
          (statusFilter === 'inactive' && !pos.isActive);
        return matchesStatus && (!query || pos.name.toLowerCase().includes(query) || pos.code.toLowerCase().includes(query));
      })
      .sort((a, b) => {
        let result = 0;
        switch (sortField) {
          case 'code':
            result = a.code.localeCompare(b.code, 'de-DE');
            break;
          case 'employeeCount':
            result = a.employeeCount - b.employeeCount;
            break;
          case 'createdAt':
            result = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            break;
          case 'updatedAt':
            result = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
            break;
          case 'name':
          default:
            result = a.name.localeCompare(b.name, 'de-DE');
        }
        return sortDirection === 'asc' ? result : -result;
      });
  }, [positions, search, statusFilter, sortField, sortDirection]);

  const breadcrumbItems = [
    { label: 'Administration', href: '/admin' },
    { label: 'Organisation', href: '/admin/organization' },
    { label: 'Positionen', current: true },
  ];

  return (
    <div className="space-y-6">
      <Breadcrumb items={breadcrumbItems} />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Positionen</h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Positionen verwalten, suchen, filtern und sortieren.
          </p>
        </div>
        <Button variant="primary" leftIcon="Plus" onClick={openCreateDialog}>
          Position anlegen
        </Button>
      </div>

      <Card className="border-neutral-200 dark:border-neutral-700">
        <CardHeader className="mb-4">
          <CardTitle>Positionenliste</CardTitle>
          <CardDescription>
            {filteredPositions.length} von {positions.length} Positionen
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && positions.length === 0 ? (
            <ErrorState description={error} onRetry={loadPositions} />
          ) : error ? (
            <Alert variant="error" title="Positionen konnten nicht vollständig geladen werden" dismissible onDismiss={() => setError(null)}>
              {error}
            </Alert>
          ) : null}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_12rem_12rem_4rem]">
            <Input
              aria-label="Positionen durchsuchen"
              placeholder="Name, Kürzel suchen ..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              leftIcon="Search"
            />
            <Select
              aria-label="Status filtern"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as PositionStatusFilter)}
              options={POSITION_STATUS_OPTIONS}
            />
            <Select
              aria-label="Positionen sortieren"
              value={sortField}
              onChange={(event) => setSortField(event.target.value as PositionSortField)}
              options={POSITION_SORT_OPTIONS}
            />
            <Button
              variant="outline"
              size="sm"
              aria-label="Sortierrichtung wechseln"
              onClick={() => setSortDirection((direction) => (direction === 'asc' ? 'desc' : 'asc'))}
            >
              {sortDirection === 'asc' ? <icons.ChevronUp className="h-4 w-4" /> : <icons.ChevronDown className="h-4 w-4" />}
            </Button>
          </div>

          {loading && positions.length === 0 ? (
            <SkeletonTable rows={6} columns={5} />
          ) : filteredPositions.length === 0 ? (
            <EmptyState
              icon="Briefcase"
              title="Keine Positionen gefunden"
              description="Passende Positionen sind nicht vorhanden oder die aktuellen Filter liefern keine Ergebnisse."
              action={
                <Button variant="outline" size="sm" onClick={() => { setSearch(''); setStatusFilter('all'); }}>
                  Filter zurücksetzen
                </Button>
              }
            />
          ) : (
            <Table
              columns={[
                {
                  key: 'name',
                  header: 'Position',
                  className: 'min-w-[180px]',
                  render: (pos: Position) => <span className="font-medium text-neutral-900 dark:text-white">{pos.name}</span>,
                },
                {
                  key: 'code',
                  header: 'Kürzel',
                  className: 'w-24',
                  render: (pos: Position) => <span className="font-mono text-sm text-neutral-600 dark:text-neutral-400">{pos.code}</span>,
                },
                {
                  key: 'employeeCount',
                  header: 'Mitarbeiter',
                  className: 'w-24',
                  render: (pos: Position) => <span className="text-sm text-neutral-700 dark:text-neutral-300">{pos.employeeCount}</span>,
                },
                {
                  key: 'isActive',
                  header: 'Status',
                  className: 'w-24',
                  render: (pos: Position) => (
                    <Badge variant={pos.isActive ? 'medical' : 'gray'}>{pos.isActive ? 'Aktiv' : 'Inaktiv'}</Badge>
                  ),
                },
                {
                  key: 'actions',
                  header: 'Aktionen',
                  className: 'w-52',
                  render: (pos: Position) => (
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); openEditDialog(pos); }}>
                        Bearbeiten
                      </Button>
                      <Button
                        variant={pos.isActive ? 'outline' : 'primary'}
                        size="sm"
                        leftIcon={pos.isActive ? 'UserX' : 'UserCheck'}
                        onClick={(e) => { e.stopPropagation(); handleToggleStatus(pos); }}
                      >
                        {pos.isActive ? 'Deaktivieren' : 'Aktivieren'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon="Trash2"
                        onClick={(e) => { e.stopPropagation(); initiateDelete(pos); }}
                      >
                        Löschen
                      </Button>
                    </div>
                  ),
                },
              ]}
              data={filteredPositions}
              keyExtractor={(pos) => pos.id}
              loading={loading && positions.length > 0}
              emptyMessage="Keine Positionen entsprechen den aktuellen Filtern."
              striped
              hoverable
              compact
            />
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={closeCreateDialog}
        title="Neue Position"
        description="Eine neue Position anlegen."
        size="md"
        closeOnBackdrop={!submitting}
        closeOnEscape={!submitting}
      >
        <form onSubmit={handleCreate} className="space-y-4">
          {formError && (
            <Alert variant="error" title="Fehler" dismissible onDismiss={() => setFormError(null)}>
              <p>{formError}</p>
            </Alert>
          )}
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="z. B. Rettungssanitäter"
            required
            autoFocus
          />
          <Input
            label="Kürzel"
            value={form.code}
            onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
            placeholder="z. B. RS"
            required
          />
          <Input
            label="Beschreibung (optional)"
            value={form.description || ''}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value || null }))}
            placeholder="Kurze Beschreibung der Position..."
          />
          <div className="flex flex-col-reverse items-stretch justify-between gap-3 border-t border-neutral-200 dark:border-neutral-700 pt-4 sm:flex-row sm:items-center">
            <Button variant="secondary" type="button" onClick={closeCreateDialog} disabled={submitting}>
              Abbrechen
            </Button>
            <Button type="submit" loading={submitting} disabled={submitting || !form.name.trim() || !form.code.trim()}>
              Speichern
            </Button>
          </div>
        </form>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={closeEditDialog}
        title={`Position bearbeiten${editingPosition ? `: ${editingPosition.name}` : ''}`}
        description="Die Positionsdaten ändern."
        size="md"
        closeOnBackdrop={!submitting}
        closeOnEscape={!submitting}
      >
        <form onSubmit={handleUpdate} className="space-y-4">
          {formError && (
            <Alert variant="error" title="Fehler" dismissible onDismiss={() => setFormError(null)}>
              <p>{formError}</p>
            </Alert>
          )}
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            required
            autoFocus
          />
          <Input
            label="Kürzel"
            value={form.code}
            onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
            required
          />
          <Input
            label="Beschreibung (optional)"
            value={form.description || ''}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value || null }))}
            placeholder="Kurze Beschreibung der Position..."
          />
          <div className="flex flex-col-reverse items-stretch justify-between gap-3 border-t border-neutral-200 dark:border-neutral-700 pt-4 sm:flex-row sm:items-center">
            <Button variant="secondary" type="button" onClick={closeEditDialog} disabled={submitting}>
              Abbrechen
            </Button>
            <Button type="submit" loading={submitting} disabled={submitting || !form.name.trim() || !form.code.trim()}>
              Speichern
            </Button>
          </div>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => { setConfirmDeleteOpen(false); setDeleteMessage(null); }}
        title="Position löschen"
        description={deleteTarget ? `Möchten Sie die Position "${deleteTarget.name}" wirklich löschen?` : ''}
        size="sm"
        closeOnBackdrop={!deleteLoading}
        closeOnEscape={!deleteLoading}
      >
        {deleteMessage && (
          <Alert variant={deleteMessage.variant} dismissible onDismiss={() => setDeleteMessage(null)}>
            <p>{deleteMessage.text}</p>
          </Alert>
        )}
        <div className="flex flex-col-reverse items-stretch justify-between gap-3 border-t border-neutral-200 dark:border-neutral-700 pt-4 sm:flex-row sm:items-center">
          <Button variant="secondary" type="button" onClick={() => { setConfirmDeleteOpen(false); setDeleteMessage(null); }} disabled={deleteLoading}>
            Abbrechen
          </Button>
          <Button variant="danger" type="button" loading={deleteLoading} onClick={executeDelete}>
            Löschen
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
